import { evidenceEn } from "./evidence";

export const en = {
  evidence: evidenceEn,
  brand: { name: "CAYLET", tool: "IP CHECK" },
  nav: {
    check: "IP Check",
    how: "How It Works",
    esim: "Proprietary eSIM",
    articles: "Articles",
    main: "Caylet.com",
    language: "Select language",
    languageCodes: { en: "EN", zhCN: "简", zhTW: "繁" },
    languageNames: { en: "English", zhCN: "简体中文", zhTW: "繁體中文" }
  },
  scanner: {
    title: "IP Reputation & Network Check",
    body: "Cloudflare observes the connection used for this request. If enabled, Scamalytics receives that same public IP for a reputation lookup.",
    start: "Start IP Check",
    rescan: "Check Again",
    loadingLocal: "Reading browser-provided details…",
    loadingNetwork: "Requesting network observations…",
    complete: "Check complete",
    partial: "Check complete with some unavailable fields",
    consent: "This page automatically performs a check of your current public IP via Cloudflare connection information.",
    privacy: "Scan results are kept in memory and will not be re-requested when switching languages.",
    statusSafe: "Scan complete. No material warning was found in the available data.",
    statusWarning: "Scan complete. Moderate network risk detected.",
    statusDanger: "Scan complete. Elevated network risk detected."
  },
  common: {
    unavailable: "Unavailable",
    yes: "Yes",
    no: "No",
    unknown: "Unknown",
    approximate: "Approximate",
    observed: "Observed",
    notObserved: "Not observed",
    close: "Close"
  },
  cards: {
    dashboardTitle: "Network & Device Diagnostic Dashboard",
    dashboardSub: "GLOBAL NETWORK & ENVIRONMENT DIAGNOSTIC MATRIX",
    connection: "Connection Summary",
    connectionSub: "CONNECTION SUMMARY",
    reputation: "IP Reputation",
    reputationSub: "IP REPUTATION",
    browser: "Browser Environment",
    browserSub: "BROWSER ENVIRONMENT",
    hardware: "Hardware & System",
    hardwareSub: "HARDWARE & SYSTEM SPECIFICATIONS",
    protocol: "Protocol Observations",
    protocolSub: "PROTOCOL OBSERVATIONS",
    ip: "Public IP",
    family: "IP Family",
    location: "IP Location",
    asn: "ASN",
    organization: "Organization",
    ipTimezone: "IP Timezone",
    score: "IP Fraud Score",
    band: "Risk Evaluation",
    flags: "Provider flags",
    providerTime: "Data time",
    abuseScore: "Abuse Confidence Score",
    abuseReports: "Abuse Report Count",
    proxy: "Proxy",
    vpn: "VPN",
    tor: "Tor",
    datacenter: "Datacenter",
    deviceTimezone: "Device timezone",
    language: "Browser language",
    browserName: "Browser",
    os: "Operating system",
    screen: "Screen",
    cores: "Logical CPU cores",
    currentFamily: "Current connection family",
    webrtc: "WebRTC observation",
    noAdditionalIp: "No additional public IP was observed during this test.",
    additionalIp: "WebRTC observed an additional public IP; review the routing context:",
    unsupported: "This browser does not expose the required WebRTC interface.",
    connectionAttribute: "IP Region",
    verifiedUS: "[Verified] US connection",
    verifiedNonUS: "[Observed] Non-US connection",
    nodeAssessment: "Node Assessment",
    nodeClear: "[Clear] All available provider flags are negative",
    nodeRisk: "[Warning] Proxy/Datacenter node",
    tzConsistency: "Timezone Consistency",
    tzMatch: "[Match] Timezone parameters align",
    tzMismatch: "[Alert] Timezone mismatch detected",
    gpu: "GPU Renderer",
    gpuStatus: "GPU Renderer Classification",
    gpuUnclassified: "[Observed] Renderer exposed; hardware type unverified",
    virtualGpu: "[Alert] Virtual/Software Emulator GPU",
    webdriver: "Automation Flag",
    webdriverDetected: "[Warning] Automated Control (WebDriver)",
    webdriverNotExposed: "[Observed] WebDriver flag not exposed",
    pingTest: "Connection Latency (Ping)",
    pingTestSub: "CONNECTION LATENCY",
    pingFast: "Fast",
    pingGood: "Good",
    pingSlow: "Slow",
    pingFailed: "Timeout",
    pingUnit: "ms",
    pingTargets: {
      cloudflareEdge: "Cloudflare Edge",
      cayletApi: "Caylet API"
    },
    pingMethod: "Measures same-origin HTTPS round-trip time from this browser to Caylet's Cloudflare edge and Worker. This is not an ICMP ping.",
    pingExplanationLabel: "Explain the connection latency measurement",
    pingExplanation: "The browser sends two small, uncached HTTPS requests to this Caylet website. The Cloudflare Edge result times a static file request, while the Caylet API result times the Worker's health endpoint. Each value is the elapsed time until the response returns, including network routing and HTTPS, Cloudflare, and Worker processing overhead. Browsers cannot send ICMP echo packets, so these values can be higher than command-line ping results and should be treated as approximate web-response latency.",
    fraudCard: {
      eyebrow: "COMPREHENSIVE ASSESSMENT",
      title: "IP Fraud Score",
      bandLow: "Low Risk",
      bandMedium: "Moderate Risk",
      bandHigh: "High Risk",
      colSource: "Data Source",
      colValue: "Parameters",
      colStatus: "Assessment",
      statusLow: "✓ Low Risk",
      statusMedium: "⚠ Moderate",
      statusHigh: "✗ High Risk",
      proxyYes: "Database finding · Proxy flag",
      proxyNo: "Not found · Proxy flag",
      asnHosting: "✗ Hosting / Datacenter",
      asnIsp: "Reference only · No fraud conclusion",
      osintEntries: "Feodo entries checked",
      osintMatch: "Database finding · Botnet C2 match",
      osintNoMatch: "Not found · This list only",
      evidenceNoData: "No data",
      note: evidenceEn.reputationNote,
      authScamalytics: "(IP reputation score source)",
      authIp2Location: "(Proxy attribute source)",
      authAbuseIPDB: "(Community abuse score source)",
      authIpinfo: "(ASN reference only)",
      authOsint: "(Botnet C2 list · CC0 OSINT)",
      providerInfoLabel: "Learn what {provider} does and why Caylet uses it",
      providerInfo: {
        scamalytics: {
          title: "What does Scamalytics do?",
          body: evidenceEn.reputationNote
        },
        ip2location: {
          title: "What does IP2Location do?",
          body: "What it does: IP2Location provides IP geolocation and network metadata, including an available-plan proxy attribute.\n\nWhy Caylet uses it: Caylet shows that proxy attribute as a separate categorical cross-check. It does not add a numeric score to the composite here, and a negative flag cannot prove that no proxy exists."
        },
        abuseIpDb: {
          title: "What does AbuseIPDB do?",
          body: evidenceEn.abuseNote
        },
        ipinfo: {
          title: "What does IPinfo.io do?",
          body: "What it does: IPinfo Lite returns basic network ownership context, including the Autonomous System Number (ASN) and AS organization name.\n\nWhy Caylet uses it: ASN context helps explain which network announces the IP address. Caylet does not treat this Lite result as a fraud score or use it in the numeric composite."
        },
        abuseCh: {
          title: "What does abuse.ch do?",
          body: "What it does: abuse.ch's Feodo Tracker publishes a recommended blocklist of IP addresses associated with tracked botnet command-and-control (C2) infrastructure.\n\nWhy Caylet uses it: Caylet downloads that list server-side and checks for an exact local match. A match is narrow positive evidence; a non-match only means this IP was not found on that list and does not prove safety. This result is not part of the numeric composite."
        }
      }
    },
    descriptions: {
      connection: "Analyzes network connection characteristics observed by Cloudflare's edge network, including public IP, protocol version (IPv4/IPv6), approximate location, ASN, and carrier organization.",
      reputation: "Shows available Scamalytics reputation scores and optional provider flags. Missing flags remain unknown; a negative or absent flag is not proof that an IP is safe. Caylet labels its own composite separately.",
      browser: "Inspects browser environment details (timezone, language, OS, screen resolution, CPU cores) and cross-checks device timezone against IP location timezone. Caylet operates in-memory without device fingerprinting.",
      protocol: "Observes the connection protocol family and performs standard WebRTC interface checks to verify if additional public IPs or routing variations exist."
    }
  },
  risk: {
    low: "Low",
    medium: "Medium",
    high: "High",
    unknown: "Unknown",
    lowText: "No obvious public IP reputation warnings were found in the available provider result.",
    mediumText: "The provider result contains signals worth reviewing in context.",
    highText: "The provider result contains elevated public reputation signals.",
    unknownText: "A reputation assessment is not available right now.",
    cayletNote: "Risk ratings are evaluated by Caylet based on composite provider signals."
  },
  diagnostics: {
    title: "Composite Risk Consistency",
    noAnomaly: "[No Material Warning] Available checks are consistent",
    incomplete: "[Incomplete Coverage] Some checks are unavailable",
    mediumRisk: "[Moderate Review] Signals require contextual review",
    highRisk: "[High Anomaly Alert] Significant risk contradiction detected",
    chipTzMatch: "Timezone Match",
    chipTzMismatch: "Timezone Mismatch",
    chipGpuUnclassified: "GPU Type Unverified",
    chipGpuVirtual: "Virtual GPU",
    chipWebDriverNotExposed: "WebDriver Not Exposed",
    chipWebDriverDetected: "WebDriver Detected",
    chipThreatClear: "No Flag in Available Data",
    chipThreatUnknown: "Threat Checks Incomplete",
    chipThreatFlagged: "Threat Flagged"
  },
  interpretation: {
    eyebrow: "RISK CONTEXT",
    title: "Read the data with context.",
    body: "These observations describe public network characteristics, not your identity or the trust decision of any financial institution.",
    corePrinciple: "Financial institutions rarely block an account based on a single anomalous signal. What truly matters is whether multiple signals are mutually consistent, and whether the current session matches the account holder's historical behavior. IP address, geolocation, and device data alone are insufficient for reliable identity verification — but combined with device trust, behavioral analysis, and transaction monitoring, they become effective risk controls.",
    signalWeightsTitle: "How signals are weighted",
    signalTiers: [
      {
        label: "High weight",
        signals: [
          "Known trusted device + app/OS integrity",
          "Passkey or hardware security key",
          "IP has abuse, proxy, or anonymizer records",
          "Impossible travel (e.g. two countries in minutes)",
          "Session token replay from unfamiliar location",
          "Post-login sensitive transaction (new payee, large transfer)",
          "Remote control or automation detected",
          "Consistent account history match"
        ]
      },
      {
        label: "Medium weight",
        signals: [
          "IP country and city (especially first-time country)",
          "ASN type (residential, mobile, datacenter, VPN)",
          "New browser or OS",
          "Login time anomaly",
          "Timezone mismatch",
          "Device language change",
          "Behavioral biometric deviation",
          "Rapid IP rotation within session"
        ]
      },
      {
        label: "Low weight (but compounds)",
        signals: [
          "Screen resolution or font list alone",
          "Standalone non-US timezone",
          "Standalone overseas IP (real travel is not fraud)",
          "Standalone browser version change",
          "Standalone public Wi-Fi usage"
        ]
      }
    ],
    networkTitle: "Network environment risk guide",
    networkEnvironments: [
      { label: "Home residential broadband", risk: "Low", note: "Long-term use, same device, same area — low risk. Home devices can still be compromised." },
      { label: "Native mobile network (major carrier)", risk: "Low–Medium", note: "Natural use case. IPs change frequently and city-level location may be inaccurate. Roaming eSIM may exit from another country." },
      { label: "Hotel / Airport / Café Wi-Fi", risk: "Low–Medium", note: "Banks mainly see the public exit IP. With a known device, official app, and MFA, account takeover risk is moderate. Adding a new payee or large transfer raises risk significantly." },
      { label: "Commercial VPN", risk: "Medium–High", note: "Known datacenter ASN, shared exit IP, anonymizer flags. Long-term consistent VPN usage may be learned as normal behavior by the risk model." },
      { label: "Datacenter / VPS exit", risk: "Medium-High–High", note: "Fraudsters easily rent bulk VPS. Unusual for retail banking customers. NIST lists cloud service IPs as a trigger for additional verification." },
      { label: "Residential proxy", risk: "Medium–High (varies)", note: "IP classified as residential does not prove the user lives there. Usage history is uncontrollable. Most dangerous when combined with location/device contradiction and rapid IP rotation." },
      { label: "Tor", risk: "Extremely High", note: "Tor exit nodes are publicly known, shared by many anonymous users, and flagged by all major financial risk systems. Explicitly listed by Microsoft as an anonymizer IP risk type." },
      { label: "Real overseas IP (genuine travel)", risk: "Context-dependent", note: "A genuine overseas IP is not fraud by itself. Risk depends on: known device, auth strength, account history, and transaction type. A real overseas mobile IP may be safer than a rotating US residential proxy." }
    ],
    scenarioTitle: "Typical Scenario Risks",
    riskFactor: "Risk Score",
    scenarioNote: "The following is not an actual score from any organization, but for illustrative purposes: the same IP can receive completely different risk evaluations under different devices, authentication methods, and transaction contexts.",
    scenarios: [
      { scenario: "Known phone + mobile network + Passkey + view balance", risk: "5–15" },
      { scenario: "Known computer + home broadband + MFA + normal use", risk: "5–20" },
      { scenario: "Known phone abroad + local mobile network", risk: "15–30" },
      { scenario: "Known device + hotel Wi-Fi + normal login", risk: "20–35" },
      { scenario: "Known device + commercial VPN + no sensitive action", risk: "30–50" },
      { scenario: "New device + new country + residential broadband + strong auth", risk: "35–55" },
      { scenario: "Datacenter VPN + new device + password + SMS OTP", risk: "55–75" },
      { scenario: "US residential proxy + location/device history contradiction", risk: "60–85" },
      { scenario: "New device + new country + recently reset password + new payee", risk: "70–90" },
      { scenario: "Rooted/emulator device + remote control + automated operation", risk: "80–95" },
      { scenario: "Tor + new device + password login + large transfer", risk: "90–100" }
    ],
    conclusion: "Many believe a 'residential IP' is the silver bullet for banking access, but this is a misconception. Modern risk systems look for a consistent chain of trust: a recognized device, a clean network, strong authentication, and habitual behavior. Rather than artificially spoofing your location, simply maintain a genuine and stable connection. In security engineering, being 'authentic and consistent' is always safer than a 'perfect disguise'.",
    limits: [
      "IP databases can be delayed or incomplete.",
      "Location is approximate and may reflect a network gateway.",
      "Dynamic and mobile-network IPs can change quickly.",
      "Financial institutions use many additional private signals not visible here.",
      "This result does not represent any platform's internal decision."
    ]
  },
  esim: {
    eyebrow: "CAYLET eSIM",
    title: "Traveling internationally? Stay connected with Caylet eSIM.",
    body: "Explore digital data plans designed for international travel. Network routes and exit locations vary by plan and carrier.",
    cta: "Explore Caylet eSIM"
  },
  education: {
    eyebrow: "HOW IT WORKS",
    title: "A clear boundary between observation and prediction",
    items: [
      {
        title: "Connection",
        body: "Cloudflare reports the public IP and network metadata visible to this site."
      },
      {
        title: "Reputation",
        body: "Scamalytics may provide a score and structured indicators from its own datasets."
      },
      {
        title: "Browser",
        body: "Your browser supplies local environment details. Caylet does not create a persistent fingerprint."
      }
    ]
  },
  faq: {
    title: "Frequently asked questions",
    items: [
      {
        q: "What does this IP check show?",
        a: "It shows the public IP observed by Cloudflare, approximate network metadata, browser-provided details, and available third-party reputation indicators."
      },
      {
        q: "Can this predict a bank or payment platform decision?",
        a: "No. Those organizations use additional private signals. This result does not represent or predict their internal review."
      },
      {
        q: "Does Caylet track my device?",
        a: "No persistent device identifier is created. Scan results remain in memory in this tab; only your language preference is stored."
      }
    ]
  },
  disclaimer: {
    title: "Disclaimer",
    body: "This educational tool reports public network characteristics and third-party database results. It cannot predict, represent, bypass or influence any bank, card issuer, payment platform or financial institution's internal controls."
  },
  recommendations: {
    sectionEyebrow: "SECONDARY EXPERIMENTAL ANALYSIS",
    sectionTitle: "Experimental Scenario Suitability",
    sectionSubtitle: "A secondary estimate based on observable network and browser signals. It is not a live access test for any platform.",
    methodologyButton: "How this tool works",
    methodologyDialog: {
      title: "How Caylet IP works",
      intro: "Caylet IP evaluates your connection by combining data from Cloudflare's edge network, third-party reputation databases, and your browser's footprint. We analyze only your 'current connection state'—we do not collect personal data, nor does this reflect any platform's definitive security decisions.",
      processTitle: "What the check observes",
      process: [
        {
          title: "Connection Info",
          body: "Extracted from Cloudflare edge nodes. This ensures authenticity, meaning you cannot manually enter an arbitrary IP for testing."
        },
        {
          title: "Reputation Sources",
          body: "Integrates threat intelligence from Scamalytics, AbuseIPDB, IP2Location, and more. All cross-referencing happens backend; raw API credentials are never exposed."
        },
        {
          title: "Browser Footprint",
          body: "Detects timezone, OS, screen resolution, hardware signals, and WebRTC status. This data is only processed instantly in-memory and is never permanently stored."
        },
        {
          title: "Regional Availability",
          body: "Cross-references your IP's region against supported areas for mainstream services like ChatGPT, Claude, and TikTok. Unlisted services are not evaluated."
        }
      ],
      fraudTitle: "IP Fraud Score",
      fraudBody: evidenceEn.reputationNote,
      recommendationTitle: "Scenario Suitability",
      recommendationBody: "We use a deterministic rules engine to assess if your network is suitable for specific scenarios (like Social Media or AI Services). Dimensions include IP reputation, network attributes, environment consistency, browser signals, and regional limits. For accuracy, if confidence falls below 40%, no numeric score is shown. At 40–59%, the score is marked as 'limited estimate'. Only with at least 85% confidence can a top-tier safety grade be achieved.",
      gradesTitle: "Score bands (shown from 40% confidence)",
      grades: [
        "85–100: Excellent suitability",
        "70–84: Good suitability",
        "55–69: Moderate / can try",
        "40–54: Elevated risk",
        "0–39: Not recommended"
      ],
      sourcesTitle: "Regional availability sources",
      sourcesIntro: "These links support only the named-service regional findings. They do not measure whether a service is reachable from this browser right now.",
      sourceVerified: "reviewed {date}",
      limitsTitle: "Important limits",
      limits: [
        "Provider records and approximate geolocation may be incomplete, delayed, or wrong.",
        "Browser observations are limited signals and do not prove identity, safety, or privacy.",
        "A negative datacenter flag does not prove that an IP is residential, mobile, or trusted.",
        "No DNS leak test is performed.",
        "Results cannot predict decisions made by banks, payment providers, social platforms, or AI services."
      ],
      modelNote: "This page explains the currently implemented experimental model (version 1.6.0). Applied factors, confidence, missing data, and score caps are shown in each recommendation card."
    },
    socialMediaTitle: "Social Media Network Suitability",
    aiSoftwareTitle: "AI Service Network Suitability",
    disclaimer: "This experimental analysis does not connect to, sign in to, or test reachability of any named platform. It is not an official platform determination; account history, device records, service policy changes, and usage behavior can produce different real-world results.",
    confidenceLabel: "Evaluation Confidence",
    confidenceModal: {
      title: "Evaluation Confidence Calculation Breakdown",
      subtitle: "Shows exact weighted contribution and cap rules used to calculate confidence.",
      rawScoreLabel: "Raw Data Coverage Weight",
      finalScoreLabel: "Final Evaluation Confidence",
      earnedLabel: "Earned",
      missingLabel: "Missing",
      items: {
        fraudScore: "Caylet Composite IP Risk Score (12%)",
        networkType: "Network Type Determination (12%)",
        proxy: "Proxy Detection Signal (8%)",
        vpn: "VPN Detection Signal (8%)",
        tor: "Tor Node Detection Signal (8%)",
        hosting: "Datacenter / Server IP Check (8%)",
        residentialProxy: "Residential Proxy Flag Check (8%)",
        ipCountry: "IP Geolocation Country (6%)",
        ipTimezone: "IP Location Timezone (5%)",
        browserTimezone: "Device System Timezone (5%)",
        automationDetected: "WebDriver Automation Check (8%)",
        webrtc: "WebRTC Address Observation (6%)"
      },
      providerCapNote: "Single threat intelligence provider cap applied (maximum confidence capped at 80%).",
      privacyCapNote: "Core privacy indicators missing (maximum confidence capped at 55%)."
    },
    detailsToggle: "View detailed factor breakdown",
    positiveTitle: "Positive Factors",
    warningTitle: "Risk Factors",
    missingTitle: "Data Gaps",
    appliedCapsTitle: "Applied Score Caps",
    capMaxScore: "Maximum score capped at {score} pts",
    methodologyTitle: "Scoring Methodology",
    methodologyBody: "This experimental heuristic evaluates scenario-specific observable dimensions. Unknown data is not treated as safe. Below 40% confidence, no numeric suitability score or meter is shown; 40–59% remains a visibly limited estimate; and the highest grade requires at least 85% confidence.",
    dimensions: {
      ipReputation: "IP Reputation Score",
      networkAuth: "Network Authenticity",
      consistency: "Environment Consistency",
      deviceIntegrity: "Device Integrity",
      protocolIntegrity: "Protocol & Leak Status",
      regionalAvailability: "Regional Availability"
    },
    grades: {
      excellent: "Excellent Suitability",
      good: "Good Suitability",
      moderate: "Moderate / Can Try",
      poor: "Elevated Risk",
      high_risk: "Not Recommended",
      insufficient_data: "Insufficient Data"
    },
    confidenceLevels: {
      high: "High Confidence",
      medium: "Moderate Confidence",
      limited: "Limited Confidence",
      insufficient: "Insufficient Data"
    },
    signals: {
      lowFraudScore: { label: "Clean IP Reputation Record", exp: "Threat database reports a low IP risk score ({score}/100) with no major abuse history." },
      elevatedFraudScore: { label: "Elevated IP Risk Score", exp: "Threat database flags an elevated risk score ({score}/100), suggesting past anomalous network activity." },
      missingFraudScore: { label: "IP Risk Score Unavailable", exp: "No valid IP risk score is available from the configured score sources." },
      osintBlocklistMatch: { label: "Botnet C2 Blocklist Match", exp: "The abuse.ch Feodo Tracker recommended list matched this IP as botnet command-and-control infrastructure. This auxiliary match is not converted into a risk probability and does not identify VPN, Proxy, Tor, or Datacenter status." },
      mobileIsp: { label: "Mobile Cellular Network", exp: "IP belongs to a mobile cellular provider, consistent with everyday mobile network usage." },
      residentialIsp: { label: "Residential Broadband Network", exp: "IP belongs to a residential broadband provider, matching everyday home user connections." },
      hostingNetwork: { label: "Datacenter / Hosting Network", exp: "IP belongs to a cloud server or hosting datacenter, not a standard residential ISP egress." },
      torNode: { label: "Tor Exit Node Detected", exp: "IP is verified as an active Tor onion network exit node with high anonymity." },
      publicProxy: { label: "Public Proxy Server", exp: "IP is flagged as a publicly accessible open proxy server." },
      proxyDetected: { label: "Proxy Connection Detected", exp: "Connection is forwarded through a proxy server." },
      residentialProxy: { label: "Residential Proxy Node", exp: "Connection is flagged as a rented residential proxy node." },
      vpnDetected: { label: "Commercial VPN Node", exp: "Connection is routed through a commercial encrypted VPN server." },
      missingProxyCheck: { label: "Proxy Status Check Unavailable", exp: "Proxy status could not be verified from current data sources." },
      missingVpnCheck: { label: "VPN Status Check Unavailable", exp: "VPN status could not be verified from current data sources." },
      missingTorCheck: { label: "Tor Status Check Unavailable", exp: "Tor node status could not be verified from current data sources." },
      missingHostingCheck: { label: "Hosting Check Unavailable", exp: "Hosting datacenter attribute could not be verified from current data sources." },
      tzMatch: { label: "Timezone Alignment Verified", exp: "Device local timezone ({tz}) matches IP geolocation timezone." },
      tzMismatch: { label: "Timezone Mismatch Detected", exp: "Device local timezone ({browserTz}) conflicts with IP location timezone ({ipTz})." },
      automationDetected: { label: "Automation Flags Detected", exp: "Browser exposes WebDriver or automated framework control flags." },
      virtualGpu: { label: "Virtual GPU / Software Renderer", exp: "Display chip identified as a software/virtual renderer ({gpu}), common in VPS or headless setups." },
      noWebRtcLeak: { label: "No Additional WebRTC Address Observed", exp: "This bounded observation did not expose another public IP. It does not prove that no leak exists." },
      webrtcLeakMismatch: { label: "WebRTC Discloses External IP", exp: "WebRTC interface discloses a secondary external IP ({ip}), mismatching request IP." },
      aiRegionRestricted: { label: "Named AI Services Restricted", exp: "For IP region {country}, official location lists show unavailable: {unavailable}." },
      aiRegionRestrictedLimited: { label: "Named AI Services Restricted", exp: "For IP region {country}, official location lists show unavailable: {unavailable}; limited listing: {limited}." },
      aiRegionPartial: { label: "Named AI Service Coverage Is Partial", exp: "For IP region {country}, official location lists show unavailable: {unavailable}; supported: {supported}." },
      aiRegionSupported: { label: "Named AI Services List This Region", exp: "Current official location lists include this region for {supported}." },
      aiRegionUnknown: { label: "AI Service Availability Unverified", exp: "No maintained named-service policy entry was evaluated for this IP region; country alone is not treated as proof." },
      socialRegionRestricted: { label: "Named Global Social Services Restricted", exp: "Current documented access findings report these services unavailable in IP region {country}: {unavailable}." },
      socialRegionPartial: { label: "TikTok Is Not Available in Hong Kong", exp: "TikTok exited the Hong Kong market. This finding does not imply that Facebook, Instagram, X, or other social platforms are unavailable." },
      socialRegionUnknown: { label: "Social Service Availability Unverified", exp: "No maintained named-service access finding was evaluated for this IP region." }
    },
    caps: {
      tor: "Tor Anonymous Onion Exit Node",
      publicProxy: "Public Open Proxy Server",
      proxy: "Proxy Forwarding Server",
      residentialProxy: "Rented Residential Proxy Node",
      vpnHosting: "Dual Datacenter & VPN Anonymization",
      automation: "WebDriver Automation Flags Detected",
      fraudHigh: "High IP Threat Score (Fraud Score >= 80)",
      fraudElevated: "Elevated IP Threat Score (Fraud Score >= 65)",
      webrtcLeak: "WebRTC Discloses Secondary External IP",
      dualStackMismatch: "IPv4 and IPv6 Egress Countries Mismatch",
      hostingSocial: "Cloud Datacenter / Hosting IP (Social Media)",
      hostingAi: "Cloud Datacenter / Hosting IP (AI Software)",
      socialServicesRestricted: "Named Global Social Services Restricted in This IP Region",
      socialServicesPartial: "TikTok Unavailable in Hong Kong",
      aiServicesRestricted: "Named AI Services Regionally Restricted",
      aiServicesPartial: "Named AI Service Coverage Is Partial"
    },
    summaries: {
      insufficient: "The available evidence is insufficient to form a reliable suitability assessment. Review the data gaps below; no numeric score is presented.",
      withWarnings: "The environment demonstrates {positives}, but is constrained by {warnings}, limiting relative suitability.",
      optimal: "No material warning was found in the signals that were available. Missing checks and provider coverage still limit this heuristic result.",
      fallbackPositives: "basic environment characteristics"
    }
  },
  privacyModal: {
    title: "Privacy Policy — Caylet IP",
    body: "Caylet IP (ip.caylet.com) is a privacy-first educational network diagnostic tool operated by Caylet LLC.\n\n1. Data Processing: We analyze the public IP address, connection metadata, and network signals observed by Cloudflare's edge network solely to generate real-time diagnostic reports.\n\n2. Client Environment: Device details (timezone, screen resolution, GPU renderer, WebRTC candidates) are processed entirely in-memory within your browser tab.\n\n3. No Persistent Tracking: We do not create persistent device fingerprints, track browsing activity, or use cross-site tracking cookies. Only your language preference is saved locally.\n\n4. Third-Party Services: Threat intelligence queries may be retrieved from Scamalytics and AbuseIPDB. No personal identity information is provided to external providers."
  },
  termsModal: {
    title: "Terms of Service — Caylet IP",
    body: "By using Caylet IP (ip.caylet.com), you agree to the following terms:\n\n1. Educational & Diagnostic Use: The network signals and risk scores provided on this site are for educational and informational reference only.\n\n2. No Guarantee of Platform Decisions: Results do not represent, predict, or influence internal risk decisions made by banks, payment providers, or financial institutions.\n\n3. Acceptable Use: Automated abuse, rate-limit bypass, or malicious scraping of this service is strictly prohibited.\n\n4. Limitation of Liability: Caylet LLC provides this diagnostic tool on an 'as-is' basis without warranties of any kind."
  },
  footer: {
    privacy: "Privacy",
    terms: "Terms",
    copyright: "© 2024-{year} Caylet LLC. All Rights Reserved.",
    privacyStatement: "This website does not collect personal data, store user fingerprints, or set non-essential cookies. Minimal requests to third parties may be made when using certain APIs, and third-party services such as analytics may be temporarily active."
  },
  articles: {
    title: "Educational Articles",
    subtitle: "Understand the fundamentals of IP addresses, network environments, proxy detection, browser fingerprinting, and platform risk control.",
    emptyState: "Educational articles are currently being compiled. You can still use the Caylet IP Check tool to analyze your current network environment.",
    backToTool: "Back to IP Check",
    readTime: "min read",
    lastUpdated: "Updated",
    published: "Published",
    related: "Related Articles",
    tableOfContents: "Table of Contents",
    sources: "Sources",
    faq: "FAQ",
    featured: "Featured Article",
    all: "All Articles"
  },
  errors: {
    generic: "The network check could not be completed. Your local browser details remain available.",
    invalid: "The service rejected an invalid request.",
    rateLimited: "Too many checks were requested. Please wait before trying again.",
    turnstile: "Please complete the verification and try again."
  },
  a11y: {
    skip: "Skip to content",
    menu: "Open navigation",
    scannerStatus: "IP check status",
    external: "Opens in a new tab",
    info: "More information"
  },
  seo: {
    title: "What Is My IP? IP Reputation & Network Check | Caylet",
    description:
      "Check your public IP, approximate location, ASN, network provider, browser environment, and Scamalytics IP reputation indicators."
  }
} as const;

export type DeepWiden<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly DeepWiden<U>[]
    : T extends object
      ? { [K in keyof T]: DeepWiden<T[K]> }
      : T;
export type Messages = DeepWiden<typeof en>;
